/**
 * Fluid-zone resolution — the one place a device, an analyzer or a display
 * surface asks "which fluid is this element in, and how dense is it".
 *
 * Moved from `packages/calc-engine/src/utils/fluid-zone-resolver.ts` (#850);
 * the frontend kept a mirror of `resolveInletFirstDensityForVertex` in
 * `pipe-geometry.ts` and a third density normaliser in `report-helpers.ts`,
 * and the three had already drifted on unit handling. Homes are in
 * CLAUDE.md: density and fluid-zone resolution live here.
 *
 * Bug history the vertex rules encode:
 *   - The naive `system.fluid_zones[0]` returned the wrong fluid on every
 *     multi-fluid Model whenever the device sat on another zone
 *     (FlowDemandDevice PR #248, PumpDevice PR #261, TankPenetrationDevice
 *     PR #262).
 *   - inlet-first devices must scan INLET edges before OUTLET edges, desktop's
 *     util::find_fluid_zone(vertex_reference) rule (find_fluid_zone.cpp);
 *     the undirected scan split CRAC's 20 identical racks into two
 *     resistance populations (+6 % flow on five of them).
 *   - Every display surface once used one Model-wide density; on a Model
 *     with water as zone 0 and two 1601.846 kg/m³ circuits, both heavy pumps
 *     displayed 5/3 of their real flow, head and power (PIPE-133).
 *
 * UNITS. `fluidZoneDensityKgM3` / `fluidZoneViscosityPaS` read the stored
 * number as-is, which is the solver's contract: zones are SI at rest (both
 * import paths normalise them, `pipe-network-adapter` writes kg/m³). The
 * solver reads the same field raw in four device paths this module does
 * not cover (PumpDevice, ControlValveDevice, FlowDemandDevice,
 * CompressorDevice), so converting here alone would make one solve use two
 * densities for one zone. A zone saved in lb/ft³ from the manual form is
 * therefore wrong on every surface by the same factor — PIPE-142 owns the
 * fix (normalise once, upstream of every reader), and when it lands every
 * reader below sees SI without changing.
 *
 * @module shared/hydraulic/fluid/fluid-zone-resolver
 */

import type { HydraulicSystem } from '../system';
import { standardGasDensityKgM3 } from '../physics/unit-conversions';

type FluidZone = HydraulicSystem['fluid_zones'][number];

/** The subset of an edge the resolvers read. Loose so callers holding a partial edge shape can pass it. */
export interface FluidZoneEdgeRef {
  fluid_zone_id?: string | undefined;
}

// ============================================================================
// Lookups
// ============================================================================

/** The zone with `zoneId`, or undefined when the id is missing or unknown. */
export function findFluidZone(
  system: Pick<HydraulicSystem, 'fluid_zones'>,
  zoneId: string | undefined,
): FluidZone | undefined {
  if (!zoneId) return undefined;
  return system.fluid_zones.find((fz) => fz.id === zoneId);
}

/**
 * Density (kg/m³) of a zone. The single sanctioned raw read of
 * `density.value` — see the module header for why it is raw.
 */
export function fluidZoneDensityKgM3(zone: Pick<FluidZone, 'id' | 'name' | 'density'>): number {
  if (!zone.density) {
    throw new Error(`fluid zone "${zone.name ?? zone.id}" has no density`);
  }
  return zone.density.value;
}

/** Dynamic viscosity (Pa·s) of a zone. Same contract as {@link fluidZoneDensityKgM3}. */
export function fluidZoneViscosityPaS(zone: Pick<FluidZone, 'id' | 'name' | 'viscosity'>): number {
  if (!zone.viscosity) {
    throw new Error(`fluid zone "${zone.name ?? zone.id}" has no viscosity`);
  }
  return zone.viscosity.value;
}

// ============================================================================
// Edge-side resolution
// ============================================================================

/** The zone an edge carries (`edge.fluid_zone_id`). No fallback: the caller decides what a missing zone means. */
export function resolveFluidZoneForEdge(
  system: Pick<HydraulicSystem, 'fluid_zones'>,
  edge: FluidZoneEdgeRef,
): FluidZone | undefined {
  return findFluidZone(system, edge.fluid_zone_id);
}

/**
 * Density (kg/m³) of the fluid an edge carries, or `fallback` when the edge
 * names no zone, the zone is unknown, or the zone has no density.
 */
export function resolveDensityForEdge(
  system: Pick<HydraulicSystem, 'fluid_zones'>,
  edge: FluidZoneEdgeRef,
  fallback: number,
): number {
  return resolveFluidZoneForEdge(system, edge)?.density?.value ?? fallback;
}

// ============================================================================
// Vertex-side resolution
// ============================================================================

/**
 * Resolve the FluidZone for a device sitting at `vertexId`. Looks for any
 * edge incident to the vertex with a `fluid_zone_id`, returning the
 * matching fluid_zone. Falls back to `fluid_zones[0]` if none found.
 *
 * Returns `undefined` only when the system has no fluid_zones at all (the
 * caller should decide whether that's an error or a default-is-acceptable
 * situation).
 */
export function resolveFluidZoneForVertex(
  system: HydraulicSystem,
  vertexId: string,
): FluidZone | undefined {
  for (const edge of system.edges) {
    if (
      edge.connection.source_id === vertexId ||
      edge.connection.target_id === vertexId
    ) {
      if (edge.fluid_zone_id) {
        const fz = system.fluid_zones.find((f) => f.id === edge.fluid_zone_id);
        if (fz) return fz;
      }
    }
  }
  return system.fluid_zones[0];
}

/**
 * Convenience: resolve density for a vertex. Returns the fallback value
 * if no fluid_zone is available (template/partial models). Callers that
 * require a real density should check for the fallback explicitly.
 */
export function resolveDensityForVertex(
  system: HydraulicSystem,
  vertexId: string,
  fallback: number,
): number {
  return resolveFluidZoneForVertex(system, vertexId)?.density?.value ?? fallback;
}

/**
 * Convenience: resolve viscosity for a vertex.
 */
export function resolveViscosityForVertex(
  system: HydraulicSystem,
  vertexId: string,
  fallback: number,
): number {
  return resolveFluidZoneForVertex(system, vertexId)?.viscosity?.value ?? fallback;
}

/**
 * Density for a device whose fluid is the one it INGESTS, mirroring desktop's
 * util::find_fluid_zone(vertex_reference) (find_fluid_zone.cpp): scan the
 * vertex's INLET edges (edge.target === vertex) first, then its OUTLET
 * edges (edge.source === vertex), returning the first RESOLVABLE edge's zone density.
 *
 * Named for the RULE, not a device class. It began as the dp-curve rule
 * (curve_loss / pressure_gain / heat_source_sink / hx_2_pipe, whose four
 * desktop translators use exactly this scan), but control valves, pressure
 * relief valves and pumps resolve the same way — a name listing device types
 * would be wrong again with the next caller.
 *
 * The undirected first-incident-edge scan in resolveDensityForVertex picked
 * the OUTLET zone for 15 of CRAC's 20 server racks (whichever edge came first
 * in document order), splitting identical racks into two resistance
 * populations (+6% flow on the inlet-resolved five). PIPE-136 is the same
 * defect on pumps.
 *
 * The same rule serves every display surface: a pump is rated on what it
 * ingests, so its flow-setpoint conversion and `H = ΔP/(ρg)` share the
 * suction density (PIPE-133). PIPE-136 moved the solver's pump onto this
 * rule too, so the two no longer disagree about WHICH zone a straddling
 * pump sits in; whether the Engineer should also be WARNED about one is
 * PIPE-149, and is still open.
 */
export function resolveInletFirstDensityForVertex(
  system: HydraulicSystem,
  vertexId: string,
  fallback: number,
): number {
  for (const side of ['inlet', 'outlet'] as const) {
    for (const edge of system.edges) {
      const hit = side === 'inlet'
        ? edge.connection.target_id === vertexId
        : edge.connection.source_id === vertexId;
      if (hit && edge.fluid_zone_id) {
        const fz = system.fluid_zones.find((f) => f.id === edge.fluid_zone_id);
        if (fz?.density) return fz.density.value;
      }
    }
  }
  // Degenerate inputs (no incident edge carries a zone): preserve the
  // legacy resolveDensityForVertex chain — first zone, then the numeric
  // fallback — so edge-less callers behave exactly as before.
  return system.fluid_zones[0]?.density?.value ?? fallback;
}

/**
 * {@link resolveInletFirstDensityForVertex} for every vertex in one pass.
 *
 * The one-shot form scans `system.edges` and searches `fluid_zones` per
 * call, so a caller that iterates vertices pays O(vertices × edges): on a
 * supported 5k-element Model that is tens of millions of main-thread checks
 * per render, and the device readout does it three times when a saved Model
 * is reopened. Building the index is O(edges + zones); a lookup is a map hit.
 *
 * Same rule, same answers: ALL inlets before any outlet (an unzoned first
 * inlet must not shadow a later zoned one), `fluid_zones[0]` ahead of the
 * numeric fallback. The equivalence is pinned by the test beside this file.
 */
export function buildInletFirstDensityIndex(
  system: HydraulicSystem,
  fallback: number,
): (vertexId: string) => number {
  const byZoneId = new Map<string, number>();
  for (const zone of system.fluid_zones ?? []) {
    const density = zone.density?.value;
    // Zones without a density are left OUT, so the edge scan below skips
    // past an edge naming one — matching the one-shot form, which keeps looking.
    if (density !== undefined) byZoneId.set(zone.id, density);
  }
  const zoneZero = system.fluid_zones?.[0]?.density?.value ?? fallback;

  // Inlets are collected separately from outlets and consulted first, because
  // the contract is "ALL inlets, then all outlets" — not "the first incident
  // edge". Within a side the earliest resolving edge wins, as in the scan.
  const inlet = new Map<string, number>();
  const outlet = new Map<string, number>();
  for (const edge of system.edges) {
    if (!edge.fluid_zone_id) continue;
    const density = byZoneId.get(edge.fluid_zone_id);
    if (density === undefined) continue;
    const { source_id: source, target_id: target } = edge.connection;
    if (!inlet.has(target)) inlet.set(target, density);
    if (!outlet.has(source)) outlet.set(source, density);
  }

  return (vertexId: string): number => inlet.get(vertexId) ?? outlet.get(vertexId) ?? zoneZero;
}

/**
 * Zone for a device whose fluid is the one it INGESTS, using desktop's
 * find_fluid_zone rule: INLET edges first, then OUTLET edges (see
 * resolveInletFirstDensityForVertex for why this is named for the rule rather
 * than a device class). Returns undefined only when no incident edge carries a
 * zone AND the system has no zones at all.
 *
 * Note it scans ALL inlets before any outlet, and skips incident edges that
 * carry no resolvable zone rather than stopping at the first one. A single
 * `.find()` would drop to fluid_zones[0] whenever an unzoned edge happened to
 * come first — the second half of the PIPE-136 defect.
 */
export function resolveInletFirstZoneForVertex(
  system: HydraulicSystem,
  vertexId: string,
): FluidZone | undefined {
  for (const side of ['inlet', 'outlet'] as const) {
    for (const edge of system.edges) {
      const hit = side === 'inlet'
        ? edge.connection.target_id === vertexId
        : edge.connection.source_id === vertexId;
      if (hit && edge.fluid_zone_id) {
        const fz = system.fluid_zones.find((f) => f.id === edge.fluid_zone_id);
        if (fz) return fz;
      }
    }
  }
  return system.fluid_zones[0];
}

// ============================================================================
// scfm standard density
// ============================================================================

/**
 * Gas standard-condition density (101325 Pa / 60 °F) for an `scfm`
 * setpoint at `vertexId`, derived from the resolved zone's
 * molecular_weight. Desktop converts scfm with the zone GAS's standard
 * density — there is no valid fluid-agnostic constant (air 1.2224, N2
 * 1.1824, …), so a missing zone or missing molecular_weight throws
 * rather than guessing.
 *
 * `zone` selection follows the same rule as the caller's density lookup:
 * pass the zone you already resolved (undirected first-incident-edge or
 * inlet-first) so flow and standard density come from the SAME
 * fluid on multi-zone systems.
 */
export function scfmStandardDensityForZone(
  zone: FluidZone | undefined,
  deviceLabel: string,
): number {
  if (!zone) {
    throw new Error(
      `${deviceLabel}: scfm setpoint requires a fluid zone (for the gas ` +
        `molecular_weight → standard density), but no zone resolved.`,
    );
  }
  if (zone.molecular_weight == null) {
    throw new Error(
      `${deviceLabel}: scfm setpoint requires the fluid zone's ` +
        `molecular_weight to compute the gas standard density, but zone ` +
        `"${zone.name ?? zone.id}" has none. No silent fallbacks.`,
    );
  }
  return standardGasDensityKgM3(zone.molecular_weight);
}

/**
 * Convenience: scfm standard density for a vertex using the undirected
 * first-incident-edge zone rule (same zone resolveDensityForVertex uses).
 */
export function resolveScfmStandardDensityForVertex(
  system: HydraulicSystem,
  vertexId: string,
  deviceLabel: string,
): number {
  return scfmStandardDensityForZone(
    resolveFluidZoneForVertex(system, vertexId),
    deviceLabel,
  );
}
